/**
 * Explore exports (POS2-885) — pure builders for the WYSIWYG CSV + the shared filename
 * convention. Kept out of the React component so the CSV shape is unit-testable; the PNG
 * rasterise + `window.print()` glue is DOM-only and lives in the ExportMenu.
 */
import type { Measure } from "@/hooks/use-explore-state";
import type { Locale } from "@/i18n/config";
import type { Granularity } from "@/lib/explore-window";
import { EXPLORE_MEASURES, type ExplorePivot, type MeasureContext } from "@/lib/explore-engine";
import { buildTableMatrix } from "@/lib/explore-chart";
import { localeDecimal } from "@/lib/format";

/**
 * How a spreadsheet in this locale expects a CSV to be written.
 *
 * Excel does not read a CSV by RFC-4180 — it reads it with the system list separator and the
 * system decimal mark, and it guesses the encoding unless the file opens with a BOM. So a
 * comma-delimited file holding `1234.5` opens in a German Excel as one garbage column, and an
 * "Überproduktion" header arrives mojibaked. Both fail silently: nobody files a bug, they stop
 * exporting (POS2-1134 pinned decision 4).
 *
 * The trigger is the locale's own decimal mark, read off `Intl` rather than tabulated — a locale
 * that writes `1,5` cannot also use a comma to separate fields. That covers German without
 * pretending German is the only such language, and leaves the English file byte-for-byte as it was.
 *
 * Gated on the *active* locale, not the browser's: the reader who chose German is the one who
 * opens the file.
 */
function csvDialect(locale: Locale): { delimiter: string; decimal: string; bom: string } {
  const decimal = localeDecimal(locale);
  if (decimal === ".") return { delimiter: ",", decimal, bom: "" };
  // Written as an escape, not the character: a literal U+FEFF is invisible in a diff.
  return { delimiter: ";", decimal, bom: "\ufeff" };
}

/** At most 2dp, trailing zeros dropped (mock §7) — raw numbers with no grouping, so the file is
 * spreadsheet-ready, in the locale's own decimal mark so the cell parses as a number. */
function num2(n: number, decimal: string): string {
  const value = String(Math.round(n * 100) / 100);
  return decimal === "." ? value : value.replace(".", decimal);
}

/** RFC-4180 field: quote + double internal quotes when it holds the delimiter, a quote, or a
 * newline (split labels — dish/kitchen names — can carry either separator). */
function csvField(value: string, delimiter: string): string {
  return value.includes(delimiter) || /["\n]/.test(value)
    ? `"${value.replace(/"/g, '""')}"`
    : value;
}

/**
 * The current pivot as CSV, mirroring the Table view cell-for-cell: a `Date` column, one
 * column per split value (labels), a per-row `Total`, and a `Total` row with the grand total.
 * Built from `buildTableMatrix` — the same engine margins the Table renders — so it can't drift.
 * Values are the active measure to 2dp (no currency symbol / kg suffix / thousands separators).
 * Unsplit → `Date` + the single measure column, no split columns (matches the Table).
 *
 * Headers and row labels come from the same catalogue and the same date formatter the Table uses,
 * so a German reader's export reads as the screen they exported it from.
 */
export function exploreCsv(
  pivot: ExplorePivot,
  measure: Measure,
  gran: Granularity,
  ctx: MeasureContext,
): string {
  const { columns, rows, colTotals, grandTotal, unsplit } = buildTableMatrix(
    pivot,
    measure,
    gran,
    ctx,
  );
  const { delimiter, decimal, bom } = csvDialect(ctx.locale);
  const total = ctx.t("waste.total");
  const lastHead = unsplit ? EXPLORE_MEASURES[measure].label(ctx) : total;

  const header = [ctx.t("waste.date"), ...columns.map((c) => c.label), lastHead];
  const body = rows.map((r) => [
    r.label,
    ...r.cells.map((n) => num2(n, decimal)),
    num2(r.total, decimal),
  ]);
  const totals = [total, ...colTotals.map((n) => num2(n, decimal)), num2(grandTotal, decimal)];

  const table = [header, ...body, totals]
    .map((row) => row.map((field) => csvField(field, delimiter)).join(delimiter))
    .join("\n");
  return bom + table;
}

/** `foodsight-{measure}-by-{granularity}.{ext}` (mock §7). */
export function exportFilename(measure: Measure, gran: Granularity, ext: "csv" | "png"): string {
  return `foodsight-${measure}-by-${gran}.${ext}`;
}
